import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { ActionIcon, Text } from '@mantine/core';
import { useToasts, type Toast, type ToastTone } from '../../store/useToasts';

/**
 * Toaster — the app's single notification surface. Mount ONCE inside
 * MantineProvider (App.tsx); everything else calls `toast.success(...)` /
 * `toast.error(...)` from `store/useToasts`.
 *
 * Fully token-driven so it reads correctly in both themes, and `role="status"` /
 * `aria-live="polite"` on the region means screen readers announce results the
 * operator previously never got at all.
 */

const TONE: Record<ToastTone, { icon: typeof Info; accent: string }> = {
  success: { icon: CheckCircle2, accent: 'rgb(var(--c-neon-ink))' },
  error: { icon: XCircle, accent: 'rgb(var(--c-red-ink))' },
  warn: { icon: AlertTriangle, accent: 'rgb(var(--c-amber-ink))' },
  info: { icon: Info, accent: 'rgb(var(--c-cyan-ink))' },
};

export function Toaster() {
  const toasts = useToasts((s) => s.toasts);
  const dismiss = useToasts((s) => s.dismiss);

  return (
    <div
      aria-live="polite"
      role="status"
      style={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 500,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: 'min(380px, calc(100vw - 32px))',
        pointerEvents: 'none',
      }}
    >
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <ToastRow key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </AnimatePresence>
    </div>
  );
}

function ToastRow({ toast: t, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const { icon: Icon, accent } = TONE[t.tone];

  useEffect(() => {
    if (t.duration <= 0) return;
    const timer = window.setTimeout(onDismiss, t.duration);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t.id, t.duration]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 16, scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 420, damping: 34 }}
      style={{
        pointerEvents: 'auto',
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        padding: '10px 12px',
        borderRadius: 12,
        background: 'rgb(var(--c-card))',
        border: '1px solid var(--c-bd)',
        borderLeft: `3px solid ${accent}`,
        boxShadow: 'var(--shadow-pop)',
      }}
    >
      <Icon size={16} color={accent} style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <Text size="sm" fw={600} c="rgb(var(--c-txt-bright))">
          {t.title}
        </Text>
        {t.message ? (
          <Text size="xs" c="dimmed" mt={2} style={{ wordBreak: 'break-word' }}>
            {t.message}
          </Text>
        ) : null}
      </div>
      <ActionIcon
        size="sm"
        variant="subtle"
        color="gray"
        aria-label="Dismiss notification"
        onClick={onDismiss}
      >
        <X size={13} />
      </ActionIcon>
    </motion.div>
  );
}
